import { useLocation } from 'react-router-dom';
import { ROUTES, generatePath } from 'src/config/routes';

export type LessonFlowStep =
  | 'subject'
  | 'photos'
  | 'evaluation'
  | 'evaluationDate'
  | 'notifications'
  | 'processing';

export const LESSON_FLOW_STEP_INDEX: Record<LessonFlowStep, number> = {
  subject: 1,
  photos: 2,
  evaluation: 3,
  evaluationDate: 4,
  notifications: 5,
  processing: 6,
};

export const LESSON_FLOW_MAX_TOTAL = 6;

export const lessonFlowTotal = ({
  hasEvaluationDate,
  asksNotifications,
}: {
  hasEvaluationDate: boolean;
  asksNotifications: boolean;
}): number => 4 + (hasEvaluationDate ? 1 : 0) + (asksNotifications ? 1 : 0);

export const lessonFlowProgress = ({
  step,
  total = LESSON_FLOW_MAX_TOTAL,
  fraction = 1,
}: {
  step: LessonFlowStep;
  total?: number;
  fraction?: number;
}): number => {
  const position =
    step === 'processing'
      ? total
      : step === 'notifications'
        ? total - 1
        : LESSON_FLOW_STEP_INDEX[step];
  return Math.min(100, ((position - 1 + fraction) / total) * 100);
};

export const lessonFlowBackPath = ({
  lessonId,
  step,
  hasEvaluationDate,
}: {
  lessonId?: string;
  step: LessonFlowStep;
  // Carried by the navigation state. Absent on a deep link, where the calendar is the screen we
  // cannot know was walked — so we do not send anyone to it.
  hasEvaluationDate?: boolean;
}): string | undefined => {
  if (step === 'photos') return ROUTES.LESSONS_SELECT_SUBJECT.path;
  if (!lessonId) return undefined;
  if (step === 'evaluationDate') {
    return generatePath('LESSON_EVALUATION_ASK', { lessonId });
  }
  if (step === 'notifications') {
    return hasEvaluationDate
      ? generatePath('LESSON_EVALUATION_DATE', { lessonId })
      : generatePath('LESSON_EVALUATION_ASK', { lessonId });
  }
  return undefined;
};

export const lessonFlowNextPath = ({
  lessonId,
  from,
  hasEvaluationDate,
  asksNotifications,
}: {
  lessonId: number | string;
  from: 'evaluation' | 'evaluationDate' | 'notifications';
  hasEvaluationDate: boolean;
  asksNotifications: boolean;
}): string => {
  const processing = generatePath('LESSON_PROCESSING', { lessonId });
  const notifications = generatePath('LESSON_NOTIFICATIONS', { lessonId });

  if (from === 'notifications') return processing;
  if (from === 'evaluationDate') return asksNotifications ? notifications : processing;
  if (hasEvaluationDate) {
    return generatePath('LESSON_EVALUATION_DATE', { lessonId });
  }
  return asksNotifications ? notifications : processing;
};

export const useLessonFlowTotal = (): number => {
  const { state } = useLocation();
  const total = (state as { flowTotal?: number } | null)?.flowTotal;
  return typeof total === 'number' && total > 0 ? total : LESSON_FLOW_MAX_TOTAL;
};
